import { createTheme, MantineColorsTuple, MantineTheme } from '@mantine/core';

// Subtle, toned-down color palette for the Reading App
// Inspired by calm, readable educational interfaces
const slate: MantineColorsTuple = [
  '#f8f9fa',
  '#f1f3f5',
  '#e9ecef',
  '#dee2e6',
  '#ced4da',
  '#adb5bd', // Primary shade - neutral, calm
  '#868e96',
  '#495057',
  '#343a40',
  '#212529',
];

// Accent color - warm, approachable teal
const teal: MantineColorsTuple = [
  '#e6fcf5',
  '#c3fae8',
  '#96f2d7',
  '#63e6be',
  '#38d9a9',
  '#20c997', // Primary shade
  '#12b886',
  '#0ca678',
  '#099268',
  '#087f5b',
];

export const theme = createTheme({
  /** Primary color - subtle teal for educational calm */
  primaryColor: 'teal',

  /** Default border radius - slightly softer */
  defaultRadius: 'md',

  /** Font family - readable and friendly */
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: 'Monaco, Courier, monospace',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600', // Slightly lighter headings
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.3' }, // Smaller, less jarring
      h2: { fontSize: '1.5rem', lineHeight: '1.35' },
      h3: { fontSize: '1.25rem', lineHeight: '1.4' },
      h4: { fontSize: '1.125rem', lineHeight: '1.45' },
      h5: { fontSize: '1rem', lineHeight: '1.5' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    },
  },

  /** Custom colors */
  colors: {
    slate,
    teal,
  },

  /** Other color customizations */
  primaryShade: 5, // Use the middle shade for primary color

  /** Light theme colors for sidebar and layout (dark mode ready) */
  other: {
    sidebarBg: slate[0], // '#f8f9fa' - Light background matching header
    sidebarBgHover: slate[2], // '#e9ecef' - Slightly darker on hover
    sidebarText: slate[7], // '#495057' - Dark gray text for readability
    sidebarTextActive: slate[9], // '#212529' - Darkest for active items
    sidebarBorder: slate[3], // '#dee2e6' - Subtle light border
    headerBg: slate[0], // '#f8f9fa' - Light header
    mainBg: slate[0], // '#f8f9fa' - Light main content (can adjust for subtle contrast)
  },

  /** Component default props - subtle styling */
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      vars: (theme: MantineTheme, props: any) => {
        const color = props.color || theme.primaryColor;

        if (props.variant === 'light' && color === 'teal') {
          return {
            root: {
              '--button-bg': theme.colors.teal[2],
              '--button-hover': theme.colors.teal[3],
              '--button-color': theme.colors.teal[9],
            },
          };
        }

        return { root: {} };
      },
      styles: (theme: MantineTheme, props: any) => {
        const variant = props.variant || 'filled';
        const color = props.color || theme.primaryColor;

        // Base styles for all buttons
        const baseStyles = {
          fontWeight: 500,
          transition: 'all 150ms ease',
          willChange: 'transform',
          '&:active': {
            transform: 'translateY(0)',
          },
        };

        // Variant-specific styles
        const variantStyles: Record<string, any> = {
          // Light variant - subtle background
          light: {
            '&:hover:not(:disabled)': {
              transform: 'translateY(-2px) !important',
              boxShadow: 'var(--mantine-shadow-sm) !important',
            },
          },
          // Filled variant - solid background
          filled: {
            '&:hover:not(:disabled)': {
              transform: 'translateY(-2px) !important',
              boxShadow: 'var(--mantine-shadow-md) !important',
              opacity: '0.9 !important',
            },
          },
          // Subtle variant - minimal styling
          subtle: {
            '&:hover:not(:disabled)': {
              transform: 'translateY(-1px) !important',
              backgroundColor: color === 'teal' ? theme.colors.teal[1] : 'rgba(0, 0, 0, 0.05)',
            },
          },
          // Outline variant
          outline: {
            '&:hover:not(:disabled)': {
              transform: 'translateY(-2px) !important',
              boxShadow: 'var(--mantine-shadow-sm) !important',
              backgroundColor: color === 'teal' ? theme.colors.teal[0] : 'rgba(0, 0, 0, 0.02)',
            },
          },
          // Default/transparent variant
          default: {
            '&:hover:not(:disabled)': {
              transform: 'translateY(-2px) !important',
              boxShadow: 'var(--mantine-shadow-xs) !important',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
            },
          },
        };

        return {
          root: {
            ...baseStyles,
            ...(variantStyles[variant] || variantStyles.default),
          },
        };
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          transition: 'transform 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 'var(--mantine-shadow-sm)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: 'var(--mantine-shadow-xs)',
          },
        },
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    Card: {
      defaultProps: {
        shadow: 'xs', // Softer shadow
        radius: 'md',
        withBorder: true,
      },
    },
    Paper: {
      defaultProps: {
        shadow: 'xs', // Softer shadow
      },
    },
    Menu: {
      styles: (theme: MantineTheme) => ({
        item: {
          transition: 'transform 0.2s ease, background-color 0.2s ease, color 0.2s ease',
          '&:hover:not([data-disabled])': {
            transform: 'translateX(2px)',
            backgroundColor: `${theme.colors.teal[1]} !important`,
            color: `${theme.colors.teal[8]} !important`,
          },
          '&[data-menu-item-color="red"]:hover:not([data-disabled])': {
            backgroundColor: `${theme.colors.red[6]} !important`,
            color: 'white !important',
          },
        },
      }),
    },
  },

  /** Breakpoints for responsive design */
  breakpoints: {
    xs: '36em',  // 576px
    sm: '48em',  // 768px
    md: '62em',  // 992px
    lg: '75em',  // 1200px
    xl: '88em',  // 1408px
  },

  /** Spacing scale */
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
});

export const createAppTheme = (_isStudent?: boolean) => theme;
